/*
 * AI 填表员演示回放视图 v3 —— 真页面缩小版回放。
 *
 * 布局对齐真实 work 页：左=对话(含输入框) | 右=文档预览(含按钮)。
 * 复用真实组件让它跟产品一致：
 *   - MessageContent(真 markdown) 渲染 AI 文本
 *   - ToolCallGroup 折叠态「处理过程 · N 个工具」
 *   - ThinkingPlaceholder「正在处理」
 *   - UserAskBlock(已提交态) 渲染真实 ask 选择器摘要
 *   - .aiClerk-chat / .aiClerk-msg + ChatPanel.scss 真实样式 + 装饰性输入框
 * 左侧文档预览用真实表格数据(用户提供的 xlsx)逐行填充。
 *
 * 播放由父级 playing/speed/runKey 驱动，rAF 推进虚拟时钟消费 demoScript 时间线。
 */
import React, { useEffect, useRef, useState } from 'react'
import DEMO_SCRIPT, { DemoScriptEvent } from './demoScript'
import DEMO_SHEET from './demoSheet'
import MessageContent from '../components/MessageContent'
import { ThinkingPlaceholder } from '../components/blocks/ToolCallGroup'
import { LoaderIcon } from '../components/blocks/icons'
import UserAskBlock from '../components/blocks/UserAskBlock'
import type { MessagePart } from '../types'
import '../components/ChatPanel.scss'
import '../components/blocks/ToolCallGroup.scss'
// 复用真实 ActionSheet 的容器样式(.aiClerk-userAskSheet__body 去掉 UserAskBlock
// 自身边框/内边距，避免"卡中卡")
import '../components/UserAskActionSheet.scss'

interface ChatItem {
  kind: 'user' | 'ai' | 'ask'
  text: string
  toolCount: number
  ask?: DemoScriptEvent['ask']
  answers?: Record<string, any>
  /** ask：是否已确认(false=可编辑态展示选项；true=已提交摘要) */
  submitted?: boolean
}

interface Props {
  playing: boolean
  speed: number
  runKey: number
  onProgress?: (ratio: number) => void
  onEnded?: () => void
}

const STEPS = ['识别表格结构', '确认填写范围', '拉取照片', '写入表格', '已完成']
const COLS = DEMO_SHEET.cols
const SAMPLE_ROWS = DEMO_SHEET.rows
const FILL_STEP = 3
const noop = () => undefined

const DemoPlaybackView: React.FC<Props> = ({
  playing,
  speed,
  runKey,
  onProgress,
  onEnded
}) => {
  const [items, setItems] = useState<ChatItem[]>([])
  const [composing, setComposing] = useState('') // 输入框正在打的字
  // 当前底部 ActionSheet 浮层里的 ask(对齐真实：ask 是浮层不是会话流卡片)
  const [activeAsk, setActiveAsk] = useState<{
    ask: DemoScriptEvent['ask']
    answers?: Record<string, any>
    submitted: boolean
  } | null>(null)
  const [stepIndex, setStepIndex] = useState(0)
  const [filledRows, setFilledRows] = useState(0)
  const [done, setDone] = useState(false)

  const askRef = useRef<typeof activeAsk>(null)
  const composeRef = useRef('')
  const cursorRef = useRef(0)
  const elapsedRef = useRef(0)
  const lastTsRef = useRef(0)
  const rafRef = useRef<number | null>(null)
  const itemsRef = useRef<ChatItem[]>([])
  const chatBodyRef = useRef<HTMLDivElement>(null)

  const events = DEMO_SCRIPT.events
  const total = DEMO_SCRIPT.totalMs

  useEffect(() => {
    cursorRef.current = 0
    elapsedRef.current = 0
    lastTsRef.current = 0
    itemsRef.current = []
    composeRef.current = ''
    askRef.current = null
    setItems([])
    setComposing('')
    setActiveAsk(null)
    setStepIndex(0)
    setFilledRows(0)
    setDone(false)
  }, [runKey])

  const lastAi = (): ChatItem | null => {
    for (let i = itemsRef.current.length - 1; i >= 0; i--) {
      if (itemsRef.current[i].kind === 'ai') return itemsRef.current[i]
    }
    return null
  }
  const countDocStatus = () => {
    let n = 0
    for (let i = 0; i < cursorRef.current; i++) {
      if (events[i].kind === 'docStatus') n++
    }
    return n - 1
  }

  const consumeUpTo = (elapsed: number) => {
    let changed = false
    let step = -1
    while (
      cursorRef.current < events.length &&
      events[cursorRef.current].t <= elapsed
    ) {
      const ev: DemoScriptEvent = events[cursorRef.current]
      cursorRef.current += 1
      changed = true
      if (ev.kind === 'composeType') {
        composeRef.current += ev.text || '' // 逐字打进输入框
      } else if (ev.kind === 'userSend') {
        itemsRef.current.push({
          kind: 'user',
          text: ev.text || '',
          toolCount: 0
        })
        composeRef.current = '' // 发送 → 清空输入框
      } else if (ev.kind === 'aiStart') {
        itemsRef.current.push({ kind: 'ai', text: '', toolCount: 0 })
        askRef.current = null // 新一轮开始 → 关掉上一轮的 ask 浮层
      } else if (ev.kind === 'aiText') {
        const cur = lastAi()
        if (cur) cur.text += ev.text || ''
      } else if (ev.kind === 'aiTool') {
        const cur = lastAi()
        if (cur) cur.toolCount += 1
      } else if (ev.kind === 'askShow') {
        // 提问 → 底部 ActionSheet 浮层(可编辑态)，不进会话流
        askRef.current = { ask: ev.ask, answers: ev.answers, submitted: false }
      } else if (ev.kind === 'askSubmit') {
        // 确认 → 关掉浮层 + 把「已确认摘要」放进会话流(对齐真实：确认结果在会话里)
        if (askRef.current) {
          itemsRef.current.push({
            kind: 'ask',
            text: '',
            toolCount: 0,
            ask: askRef.current.ask,
            answers: askRef.current.answers,
            submitted: true
          })
          askRef.current = null
        }
      } else if (ev.kind === 'docStatus') {
        step = Math.min(STEPS.length - 1, countDocStatus())
      }
    }
    if (step >= 0) setStepIndex(step)
    setComposing(composeRef.current)
    setActiveAsk(askRef.current)
    if (changed) setItems(itemsRef.current.map((m) => ({ ...m })))
    return changed
  }

  useEffect(() => {
    if (!playing || done) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      lastTsRef.current = 0
      return
    }
    const tick = (ts: number) => {
      if (!lastTsRef.current) lastTsRef.current = ts
      elapsedRef.current += (ts - lastTsRef.current) * speed
      lastTsRef.current = ts
      consumeUpTo(elapsedRef.current)
      if (onProgress) onProgress(Math.min(1, elapsedRef.current / total))
      if (cursorRef.current >= events.length) {
        askRef.current = null
        setItems(itemsRef.current.map((m) => ({ ...m })))
        setActiveAsk(null)
        setStepIndex(STEPS.length - 1)
        setFilledRows(SAMPLE_ROWS.length)
        setDone(true)
        if (onProgress) onProgress(1)
        if (onEnded) onEnded()
        return
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      lastTsRef.current = 0
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, speed, done, runKey])

  // 到「写入表格」步骤 → 逐行填充
  useEffect(() => {
    if (done) {
      setFilledRows(SAMPLE_ROWS.length)
      return
    }
    if (stepIndex < FILL_STEP) {
      setFilledRows(0)
      return
    }
    if (!playing) return
    const id = window.setInterval(
      () => {
        setFilledRows((n) => {
          if (n >= SAMPLE_ROWS.length) {
            window.clearInterval(id)
            return n
          }
          return n + 1
        })
      },
      Math.max(80, 360 / speed)
    )
    return () => window.clearInterval(id)
  }, [stepIndex, playing, speed, done])

  useEffect(() => {
    const el = chatBodyRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [items])

  const renderAi = (m: ChatItem, isLast: boolean) => {
    const streaming = isLast && !done
    const empty = !m.text && m.toolCount === 0
    return (
      <div className="aiClerk-msg aiClerk-msg--ai" data-role="assistant">
        <div className="aiClerk-msg__content">
          <div className="aiClerk-msg__main">
            <div className="aiClerk-msg__body">
              {m.toolCount > 0 && (
                <div className="aiClerk-tool-group">
                  <button type="button" className="aiClerk-tool-group__header">
                    {streaming && (
                      <LoaderIcon className="aiClerk-tool-group__spark is-spinning" />
                    )}
                    <span
                      className={
                        'aiClerk-tool-group__summary' +
                        (streaming
                          ? ' aiClerk-tool-group__summary--running'
                          : '')
                      }
                    >
                      处理过程 · {m.toolCount} 个工具
                    </span>
                  </button>
                </div>
              )}
              {m.text && <MessageContent content={m.text} />}
              {streaming && empty && <ThinkingPlaceholder label="正在处理" />}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="aiClerk-demoPlay">
      {/* 顶部 5 步进度 */}
      <div className="aiClerk-demoPlay__steps">
        {STEPS.map((s, i) => {
          const st =
            done || i < stepIndex ? 'done' : i === stepIndex ? 'active' : 'todo'
          return (
            <div key={s} className={`aiClerk-demoPlay__step is-${st}`}>
              <span className="aiClerk-demoPlay__step-dot">
                {st === 'done' ? '✓' : i + 1}
              </span>
              <span className="aiClerk-demoPlay__step-label">{s}</span>
            </div>
          )
        })}
      </div>

      <div className="aiClerk-demoPlay__split">
        {/* 左：对话(真实样式 + 装饰输入框) */}
        <div className="aiClerk-chat aiClerk-demoPlay__chat">
          <div className="aiClerk-chat__header">
            <div className="aiClerk-chat__header-avatar" aria-hidden>
              ✦
            </div>
            <span className="aiClerk-chat__header-title">AI 填表员</span>
          </div>
          <div className="aiClerk-chat__messages" ref={chatBodyRef}>
            <div className="aiClerk-chat__messages-inner">
              {items.map((m, i) => {
                if (m.kind === 'user') {
                  return (
                    <div
                      key={i}
                      className="aiClerk-msg aiClerk-msg--user"
                      data-role="user"
                    >
                      <div className="aiClerk-msg__bubble">{m.text}</div>
                    </div>
                  )
                }
                if (m.kind === 'ask' && m.ask) {
                  // 已确认摘要 → 会话流里 inline 展示(对齐真实：确认结果留在会话里)
                  return (
                    <div
                      key={i}
                      className="aiClerk-msg aiClerk-msg--ai"
                      data-role="assistant"
                    >
                      <div className="aiClerk-msg__content">
                        <div className="aiClerk-msg__main">
                          <div className="aiClerk-msg__body">
                            <UserAskBlock
                              part={
                                {
                                  type: 'user-ask',
                                  pendingId: m.ask.pendingId,
                                  title: m.ask.title,
                                  description: m.ask.description,
                                  questions: m.ask.questions
                                } as Extract<MessagePart, { type: 'user-ask' }>
                              }
                              isLastMessage={false}
                              onSubmit={noop}
                              submittedAnswers={m.answers}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }
                return (
                  <React.Fragment key={i}>
                    {renderAi(m, i === items.length - 1)}
                  </React.Fragment>
                )
              })}
            </div>
          </div>
          {/* 输入框(仿真实 composer)：回放时逐字打字 → 发送，只读不可手输 */}
          <div
            className={`aiClerk-chat__input-wrap aiClerk-demoPlay__composer${
              composing ? ' is-typing' : ''
            }`}
          >
            <textarea
              className="aiClerk-chat__input"
              placeholder="告诉 AI 填表员你想怎么改…"
              rows={2}
              value={composing}
              readOnly
            />
            <div className="aiClerk-chat__attach-btn-wrap">
              <span className="aiClerk-chat__attach-btn" aria-hidden>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 11.5l-8.5 8.5a5 5 0 01-7-7l8.5-8.5a3.5 3.5 0 015 5L10 16.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </div>
            <div className="aiClerk-chat__actions">
              <span
                className="aiClerk-chat__btn aiClerk-chat__btn--send"
                aria-hidden
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 19V5M5 12l7-7 7 7"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/* 右：文档预览(真实数据 + 装饰按钮) */}
        <div className="aiClerk-demoPlay__preview">
          <div className="aiClerk-demoPlay__preview-head">
            <span className="aiClerk-demoPlay__preview-file">
              <span className="aiClerk-demoPlay__preview-ic">📊</span>
              {DEMO_SCRIPT.docName}
            </span>
            <div className="aiClerk-demoPlay__preview-actions">
              <span
                className={`aiClerk-demoPlay__preview-badge${
                  done ? ' is-done' : ''
                }`}
              >
                {done ? '已填好' : STEPS[stepIndex]}
              </span>
              <span className="aiClerk-demoPlay__preview-btn" aria-hidden>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 12a9 9 0 11-2.6-6.4M21 3v5h-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="aiClerk-demoPlay__preview-zoom">100%</span>
            </div>
          </div>
          <div className="aiClerk-demoPlay__sheet">
            <table>
              <thead>
                <tr>
                  <th className="aiClerk-demoPlay__sheet-idx" />
                  {COLS.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SAMPLE_ROWS.map((row, ri) => {
                  const filled = ri < filledRows
                  return (
                    <tr key={ri} className={filled ? 'is-filled' : ''}>
                      <td className="aiClerk-demoPlay__sheet-idx">{ri + 1}</td>
                      {row.map((cell, ci) => (
                        <td key={ci}>{filled ? cell : ''}</td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="aiClerk-demoPlay__preview-foot">
            {done
              ? `✅ 已填入 ${SAMPLE_ROWS.length} 行 · 共 52 列`
              : `${filledRows} / ${SAMPLE_ROWS.length} 行`}
          </div>
        </div>
      </div>

      {/* ask 底部浮层(对齐真实 UserAskActionSheet)：浮在整个内容区上方，不进会话流 */}
      {activeAsk && activeAsk.ask && (
        <>
          <div className="aiClerk-demoPlay__asksheet-mask" aria-hidden />
          <div className="aiClerk-demoPlay__asksheet" role="dialog">
            <div className="aiClerk-userAskSheet__body">
              {/* 浮层只显示「提问」可编辑态；确认后的摘要在会话流里(见下方 items) */}
              <UserAskBlock
                part={
                  {
                    type: 'user-ask',
                    pendingId: activeAsk.ask.pendingId,
                    title: activeAsk.ask.title,
                    description: activeAsk.ask.description,
                    questions: activeAsk.ask.questions
                  } as Extract<MessagePart, { type: 'user-ask' }>
                }
                isLastMessage
                presentation="step"
                onSubmit={noop}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default DemoPlaybackView
